import styled, { keyframes } from "styled-components";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { cartActions } from "../../store/cart-slice";
import { ProductContext } from "../../context/ProductContext";
import AddToCartAnim from "../animations/AddToCartAnim";
import { useNavigation } from "../../utils/navigation";
import { useNavigate } from "react-router-dom";
import { calculatePrice } from "../../utils/discount";
import { useTranslation } from "react-i18next";

const ContainerX = styled.figure`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;

  background-color: var(--bg-200);
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-small);
  transition: box-shadow var(--transition-fast);
  padding: 0;
  margin: 0;
  padding: var(--spacing-md) 0;

  &:hover {
    box-shadow: var(--shadow-large);
  }
`;
const Container = styled.div`
  position: relative;
  width: 96%;
  padding: 0;
  margin: 0;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: var(--spacing-sm);
`;
const BottomContainer = styled.div`
  width: 96%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
`;

const Picture = styled.picture`
  display: flex; // Omogućavanje flex layout-a za centriranje slike
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 38%;
  max-height: 140px;
  min-height: 140px;
  position: relative;
  overflow: hidden;
  border: 1px solid var(--bg-300);
  border-radius: var(--border-radius-base);
  background-color: var(--bg-100);

  &:hover {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    .primary-image {
      display: none;
      display: ${(props) => (props.$secondary_image ? "none" : "block")};
    }

    .secondary-image {
      display: block;
      display: ${(props) => (props.$secondary_image ? "block" : "none")};
    }
  }
  @media (min-width: 768px) {
    max-height: 160px;
    min-height: 160px;
  }
`;

const Image = styled.img`
  height: 100%;
  max-height: 100%;
  max-width: 70%;
  display: block;
  margin: 0 auto;
  opacity: ${({ $outOfStock }) => ($outOfStock ? 0.45 : 1)};
  filter: ${({ $outOfStock }) => ($outOfStock ? "grayscale(1)" : "none")};
  transition: transform var(--transition-fast);

  ${Picture}:hover & {
    transform: ${({ $outOfStock }) => ($outOfStock ? "none" : "scale(1.06)")};
  }

  &.secondary-image {
    display: none;
  }
`;
const Bottom = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductTitle = styled.h3`
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--text-100);
  font-size: var(--font-size-large);
  font-weight: 700;
  cursor: pointer;
  &:hover {
    color: var(--primary-200);
  }
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const NicotineBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: var(--primary-300);
  color: var(--primary-100);
  font-size: var(--font-size-small);
  font-weight: 700;
`;

const ProductFlavor = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--bg-300);
  background-color: var(--bg-100);
  border-radius: 999px;
  padding: 3px 12px;
  font-size: var(--font-size-small);
  color: var(--text-100);
`;

const PouchInfo = styled.p`
  margin: var(--spacing-sm) 0 0 0;
  font-family: inherit;
  font-size: var(--font-size-small);
  color: var(--text-200);
  text-transform: lowercase;
`;

const OutOfStockBadge = styled.span`
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  padding: 3px 10px;
  border-radius: 999px;
  background-color: var(--bg-100);
  border: 1px solid var(--bg-300);
  color: var(--text-200);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const QuantityLabel = styled.h4`
  margin: 14px 0 6px 0;
  font-size: var(--font-size-small);
  font-weight: 600;
  color: var(--text-200);
`;

const QuantityCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const PriceCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
`;

const GridQuantity = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  padding: 4px;
  background-color: var(--bg-100);
  border: 1px solid var(--bg-300);
  border-radius: var(--border-radius-large);
`;
const GridQuantityItem = styled.button`
  flex: 1;
  padding: 7px 6px;
  font-size: var(--font-size-small);
  font-weight: 700;
  color: ${({ $isActive }) => ($isActive ? "var(--bg-100)" : "var(--text-200)")};
  background-color: ${({ $isActive }) =>
    $isActive ? "var(--primary-200)" : "var(--bg-200)"};
  border: none;
  border-radius: var(--border-radius-base);
  box-shadow: ${({ $isActive }) => ($isActive ? "var(--shadow-small)" : "none")};
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  &:hover {
    color: ${({ $isActive }) => ($isActive ? "var(--bg-100)" : "var(--primary-200)")};
    background-color: ${({ $isActive }) =>
      $isActive ? "var(--primary-200)" : "var(--bg-300)"};
  }
`;
const FlexDiv = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 12px;
`;

const QuantityPriceRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  gap: var(--spacing-lg);
`;
const ButtonWrapper = styled.div`
  position: relative; /* Roditeljski kontejner koji omogućava apsolutno pozicioniranje za decu */
  display: inline-block; /* Omogućava kontejneru da se prilagodi veličini sadržaja */
  width: 100%;
  text-align: center;
  margin-top: 14px;
  border-radius: 8px;
`;
const Button = styled.button`
  width: 100%;
  text-transform: capitalize;
  z-index: 1;
  border-radius: var(--border-radius-base);
`;

const priceFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const badgePop = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.4);
  }
  70% {
    opacity: 1;
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
`;

const TotalPrice = styled.h2`
  display: inline-block;
  margin: 4px 0 0 0;
  font-size: var(--font-size-xxlarge);
  font-weight: 700;
  color: var(--text-100);
  animation: ${priceFadeIn} 0.25s ease;
`;

const PerPiece = styled.h4`
  display: inline-block;
  margin: 0;
  font-size: var(--font-size-small);
  font-weight: 400;
  color: var(--text-200);
  animation: ${priceFadeIn} 0.25s ease;
`;

const SavingsBadge = styled.h4`
  display: inline-block;
  margin: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background-color: var(--accent-100);
  color: var(--bg-100);
  font-size: 11px;
  font-weight: 700;
  animation: ${badgePop} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const ProductCardHorizontal = ({ product }) => {
  const { t } = useTranslation();
  const [selectedQuantity, setSelectedQuantity] = useState(10);
  const dispatch = useDispatch();
  const { setIsCartOpen, maxAttributes, currencyTag } =
    useContext(ProductContext);
  const { goToProduct } = useNavigation();
  const navigate = useNavigate();

  const [isAnimating, setIsAnimating] = useState(false);

  const addToCart = (selectedQuantity) => {
    dispatch(
      cartActions.addToCart({
        product: product,
        quantity: selectedQuantity,
      })
    );
    setIsCartOpen(true);
    setIsAnimating(true);
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
  };

  const discountedPrice = calculatePrice(product.price, selectedQuantity);

  // Calculate savings
  const savingsPercentage = Math.round(
    ((product.price - discountedPrice) / product.price) * 100
  );

  const primaryImage = product.images?.find((img) => img.is_primary);
  const secondaryImage = product.images?.find((img) => !img.is_primary);
  const isOutOfStock = product.is_in_stock === "out_of_stock";
  const hasBadges = product.nicotine != null || product.flavor;

  return (
    <ContainerX>
      <Container>
        <Picture
          onClick={() =>
            navigate(goToProduct(product.category_name, product.name))
          }
          $secondary_image={secondaryImage}
        >
          {isOutOfStock && (
            <OutOfStockBadge>
              {t("BUTTONS.AVAILABLE_SOON")}
            </OutOfStockBadge>
          )}
          {primaryImage && (
            <Image
              src={primaryImage.thumbnail}
              alt={`${product.name}`}
              srcSet={`${primaryImage.thumbnail} 320w, ${primaryImage.large} 480w, ${primaryImage.large} 800w`}
              sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
              loading="lazy"
              className="primary-image"
              $outOfStock={isOutOfStock}
            />
          )}
          {secondaryImage && (
            <Image
              src={secondaryImage.thumbnail}
              alt={`${product.name}`}
              srcSet={`${secondaryImage.thumbnail} 320w, ${secondaryImage.large} 480w, ${secondaryImage.large} 800w`}
              sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
              loading="lazy"
              className="secondary-image"
              $outOfStock={isOutOfStock}
            />
          )}
        </Picture>
        <Bottom>
          <ProductTitle
            onClick={() =>
              navigate(goToProduct(product.category_name, product.name))
            }
          >
            {product.name}
          </ProductTitle>
          {hasBadges && (
            <BadgeRow>
              {product.nicotine != null && (
                <NicotineBadge>{product.nicotine}MG</NicotineBadge>
              )}
              {product.flavor && (
                <ProductFlavor>{product.flavor}</ProductFlavor>
              )}
            </BadgeRow>
          )}
          {product.pouches_per_can && (
            <PouchInfo>
              {product.pouches_per_can}x{product.format ? ` ${product.format}` : ""}{" "}
              {t("PRODUCT_CARD.POUCHES_PER_CAN")}
            </PouchInfo>
          )}
        </Bottom>
      </Container>
      <BottomContainer>
        <QuantityPriceRow>
          <QuantityCol>
            <QuantityLabel>{t("PRODUCT_CARD.CHOOSE_QUANTITY")}</QuantityLabel>
            <GridQuantity>
              {[1, 10, 30, 50].map((item, index) => (
                <GridQuantityItem
                  key={index}
                  type="button"
                  $isActive={selectedQuantity === item}
                  onClick={() => setSelectedQuantity(item)}
                >
                  {item}x
                </GridQuantityItem>
              ))}
            </GridQuantity>
          </QuantityCol>
          <PriceCol>
            <TotalPrice key={`total-${selectedQuantity}`}>
              {currencyTag}
              {(product.price * selectedQuantity).toFixed(2)}
            </TotalPrice>
            <FlexDiv>
              <PerPiece key={`piece-${selectedQuantity}`}>
                {currencyTag}
                {discountedPrice.toFixed(2)}/{t("PRODUCT_CARD.PIECE")}
              </PerPiece>
              {savingsPercentage > 0 && (
                <SavingsBadge key={`badge-${selectedQuantity}`}>
                  -{savingsPercentage}%
                </SavingsBadge>
              )}
            </FlexDiv>
          </PriceCol>
        </QuantityPriceRow>
        <ButtonWrapper>
          <Button
            onClick={() => addToCart(selectedQuantity)}
            disabled={isOutOfStock}
          >
            {!isOutOfStock && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 3H4.63158L5.72632 15.3789C5.82123 16.4547 6.72259 17.2632 7.80263 17.2632H18.4211C19.4419 17.2632 20.3097 16.5304 20.4847 15.5252L21.7895 8.05263H6.31579"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="9" cy="20.5" r="1.3" fill="currentColor" />
                <circle cx="18" cy="20.5" r="1.3" fill="currentColor" />
              </svg>
            )}
            {!isOutOfStock
              ? t("BUTTONS.ADD_TO_CART")
              : t("BUTTONS.AVAILABLE_SOON") + "!"}
          </Button>

          {isAnimating && (
            <AddToCartAnim
              isAnimating={isAnimating}
              onComplete={handleAnimationComplete}
            />
          )}
        </ButtonWrapper>
      </BottomContainer>
    </ContainerX>
  );
};

export default ProductCardHorizontal;
